import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Download, Music2, Video, Heart, Trash2, ListMusic } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getSaved, toggleSaved } from "@/lib/saved";
import SongCard from "@/components/SongCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Library() {
  const [tab, setTab] = useState("purchases");
  const [email, setEmail] = useState("");
  const [purchases, setPurchases] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedSongs, setSavedSongs] = useState([]);
  const [plOpen, setPlOpen] = useState(false);
  const [plTitle, setPlTitle] = useState("");
  const [plSaving, setPlSaving] = useState(false);
  const [plLink, setPlLink] = useState("");

  const loadSaved = async () => {
    const ids = getSaved();
    if (ids.length === 0) { setSavedSongs([]); return; }
    // fetch each; keep it simple
    const results = await Promise.all(
      ids.map((id) => api.get(`/songs/${id}`).then((r) => r.data).catch(() => null))
    );
    setSavedSongs(results.filter(Boolean));
  };

  useEffect(() => {
    loadSaved();
    const h = () => loadSaved();
    window.addEventListener("sanctum-saved-change", h);
    return () => window.removeEventListener("sanctum-saved-change", h);
  }, []);

  const load = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/library", { email });
      setPurchases(data.purchases);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch library");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="mb-10">
        <p className="eyebrow mb-3">Your library</p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight font-light">
          {tab === "saved" ? "Saved for later." : "Every song you've purchased."}
        </h1>
      </div>

      <div className="flex gap-2 mb-10 border-b border-[--sanctum-border]">
        <button
          data-testid="library-tab-purchases"
          onClick={() => setTab("purchases")}
          className={`px-4 py-3 text-sm font-medium relative -mb-px border-b-2 ${
            tab === "purchases" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-800"
          }`}
        >
          Purchases
        </button>
        <button
          data-testid="library-tab-saved"
          onClick={() => setTab("saved")}
          className={`px-4 py-3 text-sm font-medium relative -mb-px border-b-2 ${
            tab === "saved" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-800"
          }`}
        >
          <Heart size={12} strokeWidth={1.75} className="inline-block mr-1 -mt-0.5" />
          Saved ({savedSongs.length})
        </button>
      </div>

      {tab === "purchases" && (
        <>
          <form onSubmit={load} className="flex flex-col sm:flex-row gap-3 mb-10 max-w-2xl">
            <div className="relative flex-1">
              <Mail size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                data-testid="library-email"
                type="email" required
                placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-[--sanctum-border] focus:outline-none focus:border-neutral-400 text-sm"
              />
            </div>
            <button data-testid="library-lookup-btn" disabled={loading} className="btn-pill btn-primary">
              {loading ? "Looking…" : "Open my library"}
            </button>
          </form>

          {purchases !== null && (
            purchases.length === 0 ? (
              <div className="rounded-2xl border border-[--sanctum-border] p-10 text-center bg-white">
                <p className="font-display text-2xl mb-2">No purchases yet.</p>
                <p className="text-sm text-neutral-500 mb-6">Once you buy a song with this email, it will appear here forever.</p>
                <Link to="/store" className="btn-pill btn-primary text-sm">Browse the store →</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {purchases.map((p) => (
                  <div
                    key={p.session_id}
                    data-testid={`library-item-${p.session_id}`}
                    className="flex items-center gap-4 p-4 rounded-2xl border border-[--sanctum-border] bg-white hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-shadow"
                  >
                    {p.cover_url && <img src={p.cover_url} alt={p.title} className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-xl truncate">{p.title}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-1.5 mt-0.5">
                        {p.format === "mp4" ? <Video size={11} strokeWidth={1.5} /> : <Music2 size={11} strokeWidth={1.5} />}
                        {p.artist} · {p.format?.toUpperCase()} · ${p.amount?.toFixed(2)}
                        {p.tithe_amount > 0 && <span className="text-[--sanctum-accent] ml-1">· incl. ${p.tithe_amount.toFixed(2)} offering</span>}
                      </div>
                    </div>
                    {p.download_url && (
                      <a
                        href={p.download_url} target="_blank" rel="noreferrer" download
                        data-testid={`library-download-${p.session_id}`}
                        className="btn-pill btn-primary !py-2 !px-4 text-xs shrink-0"
                      >
                        <Download size={13} strokeWidth={1.5} /> Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}

      {tab === "saved" && (
        savedSongs.length === 0 ? (
          <div className="rounded-2xl border border-[--sanctum-border] p-10 text-center bg-white">
            <Heart size={28} strokeWidth={1.25} className="mx-auto text-neutral-300 mb-3" />
            <p className="font-display text-2xl mb-2">Nothing saved yet.</p>
            <p className="text-sm text-neutral-500 mb-6">Tap the heart on any song to keep it here for later.</p>
            <Link to="/songs" className="btn-pill btn-primary text-sm">Explore songs →</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-neutral-500">Group these into a shareable playlist in one click.</p>
              <button
                data-testid="make-playlist-btn"
                onClick={() => { setPlOpen(true); setPlLink(""); setPlTitle(""); }}
                className="btn-pill btn-primary text-sm"
              >
                <ListMusic size={14} strokeWidth={1.5} /> Make a playlist
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
              {savedSongs.map((s, i) => <SongCard key={s.id} song={s} index={i} />)}
            </div>
          </>
        )
      )}

      <Dialog open={plOpen} onOpenChange={setPlOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-[--sanctum-border]" data-testid="playlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal">Make a playlist</DialogTitle>
          </DialogHeader>
          {plLink ? (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">Playlist created. Share this link with anyone:</p>
              <div className="p-3 bg-[--sanctum-secondary]/40 rounded-lg text-sm break-all font-mono">{plLink}</div>
              <div className="flex gap-2 pt-2 justify-end">
                <Button variant="ghost" onClick={() => setPlOpen(false)} className="rounded-full">Close</Button>
                <Button
                  data-testid="playlist-copy-link"
                  className="rounded-full bg-neutral-900 hover:bg-black"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(plLink); toast.success("Link copied"); }
                    catch { toast.error("Could not copy"); }
                  }}
                >Copy link</Button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setPlSaving(true);
                try {
                  const { data } = await api.post("/playlists", {
                    title: plTitle || "My playlist",
                    song_ids: savedSongs.map((s) => s.id),
                  });
                  const link = `${window.location.origin}/playlists/${data.code}`;
                  setPlLink(link);
                  toast.success("Playlist ready");
                } catch (err) { toast.error(err?.response?.data?.detail || "Could not save playlist"); }
                finally { setPlSaving(false); }
              }}
              className="space-y-4"
            >
              <div>
                <label className="eyebrow block mb-1.5">Title</label>
                <Input
                  data-testid="playlist-title-input"
                  value={plTitle}
                  onChange={(e) => setPlTitle(e.target.value)}
                  placeholder="Sunday morning"
                  className="h-11 rounded-xl"
                />
              </div>
              <p className="text-xs text-neutral-500">{savedSongs.length} songs will be included.</p>
              <div className="flex justify-between pt-2">
                <Button type="button" variant="ghost" onClick={() => setPlOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="playlist-create-btn" disabled={plSaving} className="rounded-full bg-neutral-900 hover:bg-black">
                  {plSaving ? "Creating…" : "Create playlist"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
